import React, { useState, useEffect } from 'react';
import { useCurrentUser, useUpdateProfile, useUpdatePassword } from '../../hooks/useAuth';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Loading } from '../../components/ui/Loading';
import { User, Lock, Globe, Bell, Save, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDate } from '../../lib/utils';

const timezones = [
    'UTC',
    'Europe/Istanbul',
    'Europe/London',
    'Europe/Paris',
    'America/New_York',
    'America/Los_Angeles',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
];

export const Profile: React.FC = () => {
    const { data: user, isLoading } = useCurrentUser();
    const updateProfileMutation = useUpdateProfile();
    const updatePasswordMutation = useUpdatePassword();

    // Profile form state
    const [profileForm, setProfileForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        timezone: 'Europe/Istanbul',
        locale: 'en' as 'en' | 'tr',
    });

    // Password form state
    const [passwordForm, setPasswordForm] = useState({
        current_password: '',
        password: '',
        password_confirmation: '',
    });

    // Preferences state
    const [preferences, setPreferences] = useState({
        theme: 'dark' as 'light' | 'dark' | 'system',
        notifications: {
            email_alerts: true,
            critical_only: false,
            weekly_digest: true,
            new_risk_detected: true,
            payment_limit_warning: true,
        },
    });

    // Initialize form with user data
    useEffect(() => {
        if (user) {
            setProfileForm({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                phone: user.phone || '',
                timezone: user.timezone || 'Europe/Istanbul',
                locale: user.locale || 'en',
            });
            setPreferences({
                theme: user.preferences?.theme || 'dark',
                notifications: user.preferences?.notifications || {
                    email_alerts: true,
                    critical_only: false,
                    weekly_digest: false,
                    new_risk_detected: true,
                    payment_limit_warning: true,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Email cannot be changed, so exclude it from the update
            await updateProfileMutation.mutateAsync({
                first_name: profileForm.first_name,
                last_name: profileForm.last_name,
                phone: profileForm.phone,
            });
        } catch (error) {
            // Error is handled in the mutation
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (passwordForm.password !== passwordForm.password_confirmation) {
            toast.error('Passwords do not match');
            return;
        }

        if (passwordForm.password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        try {
            await updatePasswordMutation.mutateAsync(passwordForm);
            setPasswordForm({
                current_password: '',
                password: '',
                password_confirmation: '',
            });
        } catch (error) {
            // Error is handled in the mutation
        }
    };

    const handlePreferencesSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await updateProfileMutation.mutateAsync({
                timezone: profileForm.timezone,
                locale: profileForm.locale,
                preferences: preferences,
            });
        } catch (error) {
            // Error is handled in the mutation
        }
    };

    if (isLoading) {
        return <Loading fullScreen message="Loading profile..." />;
    }

    if (!user) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-600">Failed to load user profile</p>
            </div>
        );
    }

    const getPasswordChangedText = () => {
        if (!user.password_changed_at) return 'Never changed';

        const changedDate = new Date(user.password_changed_at);
        const now = new Date();
        const diffInDays = Math.floor((now.getTime() - changedDate.getTime()) / (1000 * 60 * 60 * 24));

        if (diffInDays === 0) return 'Changed today';
        if (diffInDays === 1) return 'Changed yesterday';
        if (diffInDays < 30) return `Changed ${diffInDays} days ago`;

        const diffInMonths = Math.floor(diffInDays / 30);
        if (diffInMonths === 1) return 'Changed 1 month ago';
        return `Changed ${diffInMonths} months ago`;
    };

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'owner':
                return 'bg-purple-100 text-purple-700';
            case 'admin':
                return 'bg-blue-100 text-blue-700';
            case 'developer':
                return 'bg-green-100 text-green-700';
            case 'viewer':
                return 'bg-gray-100 text-gray-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
                <p className="text-gray-600 mt-1">Manage your personal information and preferences</p>
            </div>

            {/* Profile Information */}
            <Card>
                <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                            {user.initials}
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">{user.full_name}</h2>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge className={getRoleBadgeColor(user.role)}>
                                    {user.role_label}
                                </Badge>
                                {user.email_verified && (
                                    <Badge className="bg-green-100 text-green-700">
                                        ✓ Verified
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                    <User className="w-12 h-12 text-gray-400" />
                </div>

                <form onSubmit={handleProfileSubmit} className="space-y-6">
                    {/* Name Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                First Name
                            </label>
                            <input
                                type="text"
                                value={profileForm.first_name}
                                onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Last Name
                            </label>
                            <input
                                type="text"
                                value={profileForm.last_name}
                                onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                required
                            />
                        </div>
                    </div>

                    {/* Contact Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={profileForm.email}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Email address cannot be changed
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number (Optional)
                            </label>
                            <input
                                type="tel"
                                value={profileForm.phone}
                                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                                placeholder="+90 555 123 4567"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={updateProfileMutation.isPending}
                            className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </Card>

            {/* Security Settings */}
            <Card>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Security</h2>
                        <p className="text-sm text-gray-600">
                            Password last changed: <span className="font-medium">{getPasswordChangedText()}</span>
                        </p>
                    </div>
                    <Lock className="w-12 h-12 text-gray-400" />
                </div>

                <form onSubmit={handlePasswordSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Current Password
                        </label>
                        <input
                            type="password"
                            value={passwordForm.current_password}
                            onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                New Password
                            </label>
                            <input
                                type="password"
                                value={passwordForm.password}
                                onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                required
                                minLength={8}
                            />
                            <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Confirm New Password
                            </label>
                            <input
                                type="password"
                                value={passwordForm.password_confirmation}
                                onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                required
                                minLength={8}
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={updatePasswordMutation.isPending}
                            className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            <Shield className="w-4 h-4" />
                            {updatePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                        </button>
                    </div>
                </form>
            </Card>

            {/* Preferences */}
            <Card>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Preferences</h2>
                        <p className="text-sm text-gray-600 mt-1">Customize your experience</p>
                    </div>
                    <Globe className="w-12 h-12 text-gray-400" />
                </div>

                <form onSubmit={handlePreferencesSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Timezone
                            </label>
                            <select
                                value={profileForm.timezone}
                                onChange={(e) => setProfileForm({ ...profileForm, timezone: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                {timezones.map((tz) => (
                                    <option key={tz} value={tz}>
                                        {tz}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Language
                            </label>
                            <select
                                value={profileForm.locale}
                                onChange={(e) => setProfileForm({ ...profileForm, locale: e.target.value as 'en' | 'tr' })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="en">English</option>
                                <option value="tr">Türkçe</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Theme
                        </label>
                        <div className="flex gap-4">
                            {(['light', 'dark', 'system'] as const).map((theme) => (
                                <label key={theme} className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="theme"
                                        value={theme}
                                        checked={preferences.theme === theme}
                                        onChange={(e) => setPreferences({ ...preferences, theme: e.target.value as any })}
                                        className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 capitalize">{theme}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={updateProfileMutation.isPending}
                            className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {updateProfileMutation.isPending ? 'Saving...' : 'Save Preferences'}
                        </button>
                    </div>
                </form>
            </Card>

            {/* Notification Settings */}
            <Card>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
                        <p className="text-sm text-gray-600 mt-1">Configure how you receive alerts</p>
                    </div>
                    <Bell className="w-12 h-12 text-gray-400" />
                </div>

                <form onSubmit={handlePreferencesSubmit} className="space-y-4">
                    {[
                        { key: 'email_alerts', label: 'Email Alerts', desc: 'Receive email notifications' },
                        { key: 'critical_only', label: 'Critical Alerts Only', desc: 'Only receive critical risk notifications' },
                        { key: 'weekly_digest', label: 'Weekly Digest Report', desc: 'Get a weekly summary email' },
                        { key: 'new_risk_detected', label: 'New Risk Detected', desc: 'Alert when new risks are found' },
                        { key: 'payment_limit_warning', label: 'Payment Limit Warning', desc: 'Warning when approaching limits' },
                    ].map(({ key, label, desc }) => (
                        <div key={key} className="flex items-center justify-between">
                            <div>
                                <label className="text-sm font-medium text-gray-700">{label}</label>
                                <p className="text-xs text-gray-500">{desc}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={preferences.notifications[key as keyof typeof preferences.notifications]}
                                    onChange={(e) =>
                                        setPreferences({
                                            ...preferences,
                                            notifications: {
                                                ...preferences.notifications,
                                                [key]: e.target.checked,
                                            },
                                        })
                                    }
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                            </label>
                        </div>
                    ))}

                    {/* Submit */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={updateProfileMutation.isPending}
                            className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {updateProfileMutation.isPending ? 'Saving...' : 'Save Notifications'}
                        </button>
                    </div>
                </form>
            </Card>

            {/* Account Info (Read-only) */}
            <Card>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
                </div>
                <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">User ID</span>
                        <span className="text-sm font-semibold text-gray-900">{user.id}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">Role</span>
                        <Badge className={getRoleBadgeColor(user.role)}>
                            {user.role_label}
                        </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">Email Status</span>
                        <Badge className={user.email_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {user.email_verified ? '✓ Verified' : '⚠ Not Verified'}
                        </Badge>
                    </div>
                    {user.organization && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-700">Organization</span>
                            <span className="text-sm font-semibold text-gray-900">{user.organization.name}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">Member Since</span>
                        <span className="text-sm font-semibold text-gray-900">
                            {formatDate(user.created_at)}
                        </span>
                    </div>
                </div>
            </Card>
        </div>
    );
};
